<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  INPUT_TYPES,
  formatPhoneNumber,
  formatPhoneNumberForDisplay,
  isSamePhoneNumber,
} from 'dashboard/components-next/taginput/helper/tagInputHelper.js';

import TagInput from 'dashboard/components-next/taginput/TagInput.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  contacts: {
    type: Array,
    required: true,
  },
  selectedContact: {
    type: Object,
    default: null,
  },
  showContactsDropdown: {
    type: Boolean,
    required: true,
  },
  isLoading: {
    type: Boolean,
    required: true,
  },
  isCreatingContact: {
    type: Boolean,
    required: true,
  },
  contactId: {
    type: String,
    default: null,
  },
  contactableInboxesList: {
    type: Array,
    default: () => [],
  },
  showInboxesDropdown: {
    type: Boolean,
    required: true,
  },
  hasErrors: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'searchContacts',
  'setSelectedContact',
  'clearSelectedContact',
  'updateDropdown',
]);

const i18nPrefix = 'COMPOSE_NEW_CONVERSATION.FORM.CONTACT_SELECTOR';
const { t } = useI18n();

const inputType = ref(INPUT_TYPES.EMAIL);

// Chat Storm: o telefone aparece sempre no formato local, sem o "+55", e o
// rotulo usa "Nome - Telefone" (o numero ja tem parenteses no DDD, entao os
// parenteses de fora deixavam "Nome ((99) 91234-5678)"). Quando o nome e o
// proprio numero, mostra so o numero formatado em vez de repeti-lo.
const buildContactLabel = ({ name, email, phoneNumber }) => {
  if (email) return `${name} (${email})`;
  if (!phoneNumber) return name || '';

  const displayPhone = formatPhoneNumberForDisplay(phoneNumber);
  return isSamePhoneNumber(name, phoneNumber)
    ? displayPhone
    : `${name} - ${displayPhone}`;
};

const contactsList = computed(() => {
  return props.contacts?.map(({ name, id, thumbnail, email, ...rest }) => ({
    id,
    label: buildContactLabel({ name, email, phoneNumber: rest.phoneNumber }),
    value: id,
    thumbnail: { name, src: thumbnail },
    ...rest,
    name,
    email,
    action: 'contact',
  }));
});

const selectedContactLabel = computed(() =>
  buildContactLabel(props.selectedContact || {})
);

const errorClass = computed(() => {
  return props.hasErrors
    ? '[&_input]:placeholder:!text-n-ruby-9 [&_input]:dark:placeholder:!text-n-ruby-9'
    : '';
});

// Chat Storm: no original so era telefone quando comecava com '+'. Aqui qualquer
// texto formado apenas por numeros (e separadores) e tratado como telefone, para
// o cliente poder digitar so o DDD + numero (ex.: 99 91234-5678).
const looksLikePhoneNumber = value =>
  /\d/.test(value) && /^[+\d\s()-]+$/.test(value);

const handleInput = value => {
  const trimmedValue = value.trim();

  if (looksLikePhoneNumber(trimmedValue)) {
    inputType.value = INPUT_TYPES.TEL;
    // Busca pelo numero ja normalizado (+55...), que e como ele fica salvo
    const { isValid, e164Value } = formatPhoneNumber(trimmedValue);
    emit('searchContacts', isValid ? e164Value : trimmedValue);
    return;
  }

  inputType.value = INPUT_TYPES.EMAIL;
  emit('searchContacts', value);
};
</script>

<template>
  <div class="relative flex-1 px-4 py-3 overflow-y-visible">
    <div class="flex items-baseline w-full gap-3 min-h-7">
      <label class="text-sm font-medium text-n-slate-11 whitespace-nowrap">
        {{ t(`${i18nPrefix}.LABEL`) }}
      </label>

      <div
        v-if="isCreatingContact"
        class="flex items-center gap-1.5 rounded-md bg-n-alpha-2 px-3 min-h-7 min-w-0"
      >
        <span class="text-sm truncate text-n-slate-12">
          {{ t(`${i18nPrefix}.CONTACT_CREATING`) }}
        </span>
      </div>
      <div
        v-else-if="selectedContact"
        class="flex items-center gap-1.5 rounded-md bg-n-alpha-2 min-h-7 min-w-0"
        :class="!contactId ? 'ltr:pl-3 rtl:pr-3 ltr:pr-1 rtl:pl-1' : 'px-3'"
      >
        <span class="text-sm truncate text-n-slate-12">
          {{
            isCreatingContact
              ? t(`${i18nPrefix}.CONTACT_CREATING`)
              : selectedContactLabel
          }}
        </span>
        <Button
          v-if="!contactId"
          variant="ghost"
          icon="i-lucide-x"
          color="slate"
          :disabled="contactId"
          size="xs"
          @click="emit('clearSelectedContact')"
        />
      </div>
      <TagInput
        v-else
        :placeholder="t(`${i18nPrefix}.TAG_INPUT_PLACEHOLDER`)"
        mode="single"
        :menu-items="contactsList"
        :show-dropdown="showContactsDropdown"
        :is-loading="isLoading"
        :disabled="contactableInboxesList?.length > 0 && showInboxesDropdown"
        allow-create
        :type="inputType"
        class="flex-1 min-h-7"
        :class="errorClass"
        focus-on-mount
        @input="handleInput"
        @on-click-outside="emit('updateDropdown', 'contacts', false)"
        @add="emit('setSelectedContact', $event)"
        @remove="emit('clearSelectedContact')"
      />
    </div>
  </div>
</template>
